import { useState, useEffect, useRef, useMemo } from "react";
import {
  LayoutDashboard, BarChart2, BookOpen, Video, Megaphone,
  Users, CreditCard, Mic, Heart, Smartphone, Bell, Search,
  Menu, LogOut, X, Settings, Eye, EyeOff, UserCircle, Lock,
  ChevronsLeft, ChevronsRight, RefreshCw, Sun, Moon, Monitor, Camera,
  Palette, Globe, Loader2, Star, ChevronRight, Download, ScrollText,
  ClipboardList, MapPin, Activity, Upload,
} from "lucide-react";

import { STYLES } from "./lib/styles.js";
import { auth as authApi, setToken, getToken } from "./lib/api.js";
import { useApi, useToast } from "./hooks/index.js";
import { admin as adminApi } from "./lib/api.js";
import { useLang } from "./lib/i18n.jsx";
import { trackPageview, trackEvent } from "./lib/track.js";

// ─── THEME HELPERS ────────────────────────────────────────────────────────────

function applyTheme(theme) {
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isDark = theme === "dark" || (theme === "system" && prefersDark);
  document.documentElement.classList.toggle("dark", isDark);
}

function useTheme() {
  const [theme, setThemeState] = useState(
    () => localStorage.getItem("kb_theme") ?? "system"
  );
  useEffect(() => {
    applyTheme(theme);
    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme("system");
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
  }, [theme]);
  const setTheme = (t) => {
    setThemeState(t);
    localStorage.setItem("kb_theme", t);
  };
  return [theme, setTheme];
}

import { Toast, Spinner } from "./components/ui.jsx";

import PageDashboard from "./pages/Dashboard.jsx";
import PageAnalytics from "./pages/Analytics.jsx";
import PageVisitorAnalytics from "./pages/VisitorAnalytics.jsx";
import PageUsers, { PageUserDetail } from "./pages/Users.jsx";
import {
  PageModules, PageVideos, PageAnnouncements,
  PageSubscriptions, PageAskGad, PageDonations, PageMobileMoney,
  PageModuleDetail, PagePricing, PageCardPayments, PageTransactions, PageManualPayments,
} from "./pages/index.jsx";
import { PagePrivacyPolicy, PageTermsOfService, PageSupport } from "./pages/Legal.jsx";
import PageSupportTeam from "./pages/SupportTeam.jsx";
import PageRoles from "./pages/Roles.jsx";
import PageAuditLog from "./pages/AuditLog.jsx";
import PageAssessments from "./pages/Assessments.jsx";
import PageCountryAvailability from "./pages/CountryAvailability.jsx";

// ─── NAV CONFIG (static IDs + translation keys) ───────────────────────────────

const NAV_CFG = [
  { sectionKey: "nav.overview", items: [
    { id: "dashboard",     icon: LayoutDashboard, labelKey: "nav.dashboard",     permission: "VIEW_DASHBOARD" },
    { id: "analytics",     icon: BarChart2,       labelKey: "nav.analytics",     permission: "VIEW_ANALYTICS" },
    { id: "visitors",      icon: Globe,           labelKey: "nav.visitors",      permission: "VIEW_ANALYTICS" },
  ]},
  { sectionKey: "nav.content", items: [
    { id: "modules",       icon: BookOpen,        labelKey: "nav.modules",       permission: ["VIEW_CONTENT", "MANAGE_CONTENT"] },
    { id: "videos",        icon: Video,           labelKey: "nav.videos",        permission: "MANAGE_VIDEOS" },
    { id: "announcements", icon: Megaphone,       labelKey: "nav.announcements", permission: ["VIEW_CONTENT", "MANAGE_ANNOUNCEMENTS"] },
  ]},
  { sectionKey: "nav.users", items: [
    { id: "users",         icon: Users,           labelKey: "nav.allUsers",      permission: "VIEW_USERS" },
    { id: "subscriptions", icon: CreditCard,      labelKey: "nav.subscriptions", permission: ["VIEW_SUBSCRIPTIONS", "MANAGE_SUBSCRIPTIONS"] },
    { id: "askgad",        icon: Mic,             labelKey: "nav.askGad",        permission: "VIEW_QUESTIONS" },
    { id: "assessments",   icon: ClipboardList,   labelKey: "nav.assessments",   permission: "VIEW_ASSESSMENTS" },
  ]},
  { sectionKey: "nav.revenue", items: [
    { id: "donations",      icon: Heart,           labelKey: "nav.donations",       permission: "VIEW_DONATIONS" },
    { id: "mobilemoney",    icon: Smartphone,      labelKey: "nav.mobileMoney",     permission: "VIEW_MOBILE_MONEY" },
    { id: "cardpay",        icon: CreditCard,      labelKey: "nav.cardPayments",    permission: "VIEW_CARD_PAYMENTS" },
    { id: "manualpayments", icon: Upload,          labelKey: "nav.manualPayments",  permission: "MANAGE_SUBSCRIPTIONS" },
    { id: "transactions",  icon: Activity,        labelKey: "nav.transactions",  permission: ["VIEW_SUBSCRIPTIONS", "MANAGE_SUBSCRIPTIONS"] },
    { id: "pricing",       icon: Settings,        labelKey: "nav.pricing",       permission: ["VIEW_DASHBOARD", "MANAGE_PRICING"] },
  ]},
  { sectionKey: "nav.administration", items: [
    { id: "supportteam",   icon: Users,           labelKey: "nav.supportTeam",   permission: ["MANAGE_SUPPORT_TEAM", "VIEW_SUPPORT_TEAM"] },
    { id: "roles",         icon: Lock,            labelKey: "nav.rolesPermissions", permission: "MANAGE_ROLES" },
    { id: "auditlog",      icon: ScrollText,      labelKey: "nav.auditLogs",      permission: "VIEW_AUDIT_LOGS" },
    { id: "countries",     icon: MapPin,           labelKey: "nav.countries",       permission: ["VIEW_COUNTRIES", "MANAGE_COUNTRIES"] },
  ]},
];

// ─── ACCOUNT TABS CONFIG ─────────────────────────────────────────────────────

const ACCT_TABS_CFG = [
  { id: "profile",    labelKey: "acct.profile",    icon: UserCircle },
  { id: "security",   labelKey: "acct.security",   icon: Lock       },
  { id: "appearance", labelKey: "acct.appearance", icon: Palette    },
  { id: "language",   labelKey: "acct.language",   icon: Globe      },
  { id: "session",    labelKey: "acct.session",    icon: Settings   },
];

// ─── LANGUAGE OPTIONS ─────────────────────────────────────────────────────────

const LANGUAGES = [
  { code: "en",  flag: "🇬🇧", nativeName: "English",      enName: "English"     },
  { code: "fr",  flag: "🇫🇷", nativeName: "Français",     enName: "French"      },
  { code: "kin", flag: "🇷🇼", nativeName: "Ikinyarwanda", enName: "Kinyarwanda" },
];

// ─── RESET PASSWORD PAGE (public — no auth required) ─────────────────────────

function ResetPassword({ token, onBack }) {
  const [newPw,     setNewPw]     = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showNew,   setShowNew]   = useState(false);
  const [showConf,  setShowConf]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState(null);
  const [newFocus,  setNewFocus]  = useState(false);
  const [confFocus, setConfFocus] = useState(false);

  // Password strength
  const getStrength = (pw) => {
    if (!pw) return null;
    if (pw.length < 6)  return { bars: 1, color: "#ef4444", label: "Too short"  };
    if (pw.length < 8)  return { bars: 2, color: "#f59e0b", label: "Weak"       };
    const score = [/[A-Z]/.test(pw), /[0-9]/.test(pw), /[^A-Za-z0-9]/.test(pw)].filter(Boolean).length;
    if (score >= 3)     return { bars: 4, color: "#10b981", label: "Strong"     };
    if (score === 2)    return { bars: 3, color: "#3b82f6", label: "Good"       };
    return               { bars: 2, color: "#f59e0b", label: "Weak"             };
  };

  const strength     = getStrength(newPw);
  const pwMatch      = confirmPw.length > 0 && newPw === confirmPw;
  const pwMismatch   = confirmPw.length > 0 && newPw !== confirmPw;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!newPw || newPw.length < 8)   { setError("Password must be at least 8 characters.");  return; }
    if (newPw !== confirmPw)          { setError("Passwords do not match.");                    return; }
    setLoading(true);
    try {
      await authApi.resetPassword(token, newPw);
      setDone(true);
      // Clean the token from the URL without a page reload
      window.history.replaceState({}, document.title, "/");
    } catch (err) {
      setError(err?.message ?? "Invalid or expired reset link. Please request a new one.");
    } finally {
      setLoading(false);
    }
  };

  const inp = (focused, invalid) => ({
    width: "100%", padding: "12px 44px 12px 16px", borderRadius: 10, fontSize: 14,
    fontFamily: "var(--fb)", color: "#111",
    background: invalid ? "#fef2f2" : focused ? "#fff" : "#f7faf8",
    border: `1.5px solid ${invalid ? "#ef4444" : focused ? "#0db87e" : "#d1d5db"}`,
    boxShadow: focused && !invalid ? "0 0 0 3px rgba(13,184,126,.13)" : "none",
    outline: "none", transition: "all .15s", boxSizing: "border-box",
  });

  const year = new Date().getFullYear();

  return (
    <div className="imigongo-bg" style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "32px 16px 24px",
      background: "#07120e",
      backgroundImage: "radial-gradient(ellipse 90% 55% at 50% 0%, rgba(13,184,126,.13) 0%, transparent 70%)",
    }}>

      {/* Brand */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 18, margin: "0 auto 14px",
          background: "#ffffff", padding: 8,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 26, boxShadow: "0 8px 32px rgba(13,184,126,.4)",
        }}><img src="/icon.png" style={{ width: 38, height: 38, objectFit: "contain" }} alt="Kunga Basics" /></div>
        <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 20, color: "#fff", letterSpacing: "-.3px" }}>
          Kunga Basics
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,.38)", marginTop: 4, letterSpacing: ".4px" }}>
          PASSWORD RESET
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: "#fff", borderRadius: 20, padding: "36px 36px 28px",
        width: "100%", maxWidth: 420,
        boxShadow: "0 32px 80px rgba(0,0,0,.55), 0 8px 24px rgba(0,0,0,.25)",
      }}>

        {done ? (
          /* ── Success state ── */
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%", margin: "0 auto 20px",
              background: "linear-gradient(135deg, rgba(16,185,129,.15), rgba(16,185,129,.05))",
              border: "2px solid rgba(16,185,129,.3)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34,
            }}>✅</div>
            <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 20, color: "#0f1a14", marginBottom: 10 }}>
              Password updated!
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, marginBottom: 24 }}>
              Your password has been changed successfully. You can now sign in with your new password.
            </div>
            <button
              onClick={onBack}
              style={{
                width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
                background: "linear-gradient(135deg, #0db87e 0%, #089962 100%)",
                color: "#fff", fontFamily: "var(--fb)", fontSize: 15, fontWeight: 700,
                cursor: "pointer", boxShadow: "0 4px 18px rgba(13,184,126,.38)",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              → Sign in now
            </button>
          </div>
        ) : (
          /* ── Form state ── */
          <>
            <div style={{ marginBottom: 26 }}>
              <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 20, color: "#0f1a14", letterSpacing: "-.3px", marginBottom: 5 }}>
                Set new password
              </div>
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                Choose a strong password for your account.
              </div>
            </div>

            <form onSubmit={submit}>

              {/* New password */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, letterSpacing: ".5px", textTransform: "uppercase" }}>
                  New password
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showNew ? "text" : "password"}
                    value={newPw}
                    required
                    autoFocus
                    onChange={e => setNewPw(e.target.value)}
                    onFocus={() => setNewFocus(true)}
                    onBlur={() => setNewFocus(false)}
                    placeholder="At least 8 characters"
                    style={{ ...inp(newFocus, false), paddingRight: 46 }}
                  />
                  <button type="button" tabIndex={-1} onClick={() => setShowNew(v => !v)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: showNew ? "#0db87e" : "#9ca3af", display: "flex", padding: 4 }}>
                    {showNew ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>

                {/* Strength bars */}
                {strength && (
                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ display: "flex", gap: 3, flex: 1 }}>
                      {[1,2,3,4].map(n => (
                        <div key={n} style={{
                          flex: 1, height: 4, borderRadius: 2,
                          background: n <= strength.bars ? strength.color : "#e5e7eb",
                          transition: "background .2s",
                        }} />
                      ))}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: strength.color, minWidth: 52 }}>
                      {strength.label}
                    </span>
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div style={{ marginBottom: 22 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, letterSpacing: ".5px", textTransform: "uppercase" }}>
                  Confirm password
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showConf ? "text" : "password"}
                    value={confirmPw}
                    required
                    onChange={e => setConfirmPw(e.target.value)}
                    onFocus={() => setConfFocus(true)}
                    onBlur={() => setConfFocus(false)}
                    placeholder="Repeat new password"
                    style={{ ...inp(confFocus, pwMismatch), paddingRight: 46 }}
                  />
                  <button type="button" tabIndex={-1} onClick={() => setShowConf(v => !v)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: showConf ? "#0db87e" : "#9ca3af", display: "flex", padding: 4 }}>
                    {pwMatch
                      ? <span style={{ fontSize: 16, color: "#10b981" }}>✓</span>
                      : showConf ? <EyeOff size={17} /> : <Eye size={17} />
                    }
                  </button>
                </div>
                {pwMismatch && (
                  <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>Passwords do not match</div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div style={{
                  background: "#fef2f2", color: "#b91c1c", border: "1px solid #fca5a5",
                  borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 18,
                  display: "flex", alignItems: "center", gap: 8, fontWeight: 500,
                }}>
                  ⚠️ {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !newPw || !confirmPw}
                style={{
                  width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
                  background: loading || !newPw || !confirmPw
                    ? "#e5e7eb"
                    : "linear-gradient(135deg, #0db87e 0%, #089962 100%)",
                  color: loading || !newPw || !confirmPw ? "#9ca3af" : "#fff",
                  fontFamily: "var(--fb)", fontSize: 15, fontWeight: 700,
                  cursor: loading || !newPw || !confirmPw ? "not-allowed" : "pointer",
                  boxShadow: !loading && newPw && confirmPw ? "0 4px 18px rgba(13,184,126,.38)" : "none",
                  transition: "all .2s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                {loading
                  ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Updating password…</>
                  : <>🔐 Set new password</>
                }
              </button>
            </form>

            {/* Back to login */}
            <div style={{ marginTop: 18, textAlign: "center" }}>
              <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#9ca3af", fontFamily: "var(--fb)" }}>
                ← Back to login
              </button>
            </div>
          </>
        )}
      </div>

      <div style={{ marginTop: 28, fontSize: 11, color: "rgba(255,255,255,.2)", textAlign: "center" }}>
        © {year} Kunga Basics Ltd. All rights reserved.
      </div>
    </div>
  );
}

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────

function Login({ onLogin, modal = false }) {
  const { t } = useLang();
  const [email, setEmail]           = useState("");
  const [pw, setPw]                 = useState("");
  const [showPw, setShowPw]         = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [emailFocus, setEmailFocus] = useState(false);
  const [pwFocus, setPwFocus]       = useState(false);

  // MFA challenge step — set when /auth/login responds with mfaRequired
  const [mfaChallenge, setMfaChallenge] = useState(null); // { mfaToken, maskedEmail }
  const [otp, setOtp]                   = useState("");
  const [otpFocus, setOtpFocus]         = useState(false);
  const [verifying, setVerifying]       = useState(false);
  const [resending, setResending]       = useState(false);
  const [resent, setResent]             = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const r = await authApi.login(email, pw);
      if (r?.mfaRequired) {
        setMfaChallenge({ mfaToken: r.mfaToken, maskedEmail: r.maskedEmail });
        return;
      }
      if (r?.user?.role !== "ADMIN") { setError(t("login.adminOnly")); return; }
      setToken(r.accessToken, r.refreshToken);
      onLogin(r.user);
    } catch (err) {
      setError(err.message || t("login.adminOnly"));
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6 || verifying) return;
    setVerifying(true); setError(null);
    try {
      const r = await authApi.verifyMfa(mfaChallenge.mfaToken, otp);
      if (r?.user?.role !== "ADMIN") { setError(t("login.adminOnly")); return; }
      setToken(r.accessToken, r.refreshToken);
      onLogin(r.user);
    } catch (err) {
      setError(err.message || t("login.adminOnly"));
    } finally { setVerifying(false); }
  };

  const resendOtp = async () => {
    if (resending) return;
    setResending(true); setError(null); setResent(false);
    try {
      const r = await authApi.resendMfa(mfaChallenge.mfaToken);
      setMfaChallenge((c) => ({ ...c, mfaToken: r.mfaToken }));
      setOtp("");
      setResent(true);
      setTimeout(() => setResent(false), 2500);
    } catch (err) {
      setError(err.message || t("login.adminOnly"));
    } finally { setResending(false); }
  };

  const inp = (focused) => ({
    width: "100%", padding: "12px 16px", borderRadius: 10, fontSize: 14,
    fontFamily: "var(--fb)", color: "#111", background: focused ? "#fff" : "#f7faf8",
    border: `1.5px solid ${focused ? "#0db87e" : "#d1d5db"}`,
    boxShadow: focused ? "0 0 0 3px rgba(13,184,126,.13)" : "none",
    outline: "none", transition: "all .15s", boxSizing: "border-box",
  });

  const year = new Date().getFullYear();

  const card = (
      <div style={{
        background: "#ffffff", borderRadius: 20, padding: "36px 36px 28px",
        width: "100%", maxWidth: 420,
        boxShadow: "0 32px 80px rgba(0,0,0,.55), 0 8px 24px rgba(0,0,0,.25), 0 0 0 1px rgba(255,255,255,.06)",
      }}>

        {/* Card title */}
        <div style={{ marginBottom: 26 }}>
          <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 20, color: "#0f1a14", letterSpacing: "-.3px", marginBottom: 5 }}>
            {mfaChallenge ? t("login.otpTitle") : t("login.signIn")}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            {mfaChallenge
              ? t("login.otpSubtitle", { email: mfaChallenge.maskedEmail })
              : t("login.subtitle")}
          </div>
        </div>

        {mfaChallenge ? (
        <form onSubmit={verifyOtp}>

          {/* OTP code */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, letterSpacing: ".5px", textTransform: "uppercase" }}>
              {t("login.otpCode")}
            </label>
            <input
              type="text" inputMode="numeric" autoComplete="one-time-code"
              data-testid="mfa-otp-input"
              value={otp} required maxLength={6}
              onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onFocus={() => setOtpFocus(true)}
              onBlur={() => setOtpFocus(false)}
              placeholder="000000"
              style={{ ...inp(otpFocus), letterSpacing: "6px", textAlign: "center", fontSize: 20, fontFamily: "var(--fm)" }}
            />
          </div>

          {/* Error banner */}
          {error && (
            <div style={{
              background: "#fef2f2", color: "#b91c1c",
              border: "1px solid #fca5a5", borderRadius: 10,
              padding: "10px 14px", fontSize: 13, marginBottom: 18,
              display: "flex", alignItems: "center", gap: 8, fontWeight: 500,
            }}>
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Resent confirmation */}
          {resent && !error && (
            <div style={{
              background: "#f0fdf4", color: "#15803d",
              border: "1px solid #bbf7d0", borderRadius: 10,
              padding: "10px 14px", fontSize: 13, marginBottom: 18,
              display: "flex", alignItems: "center", gap: 8, fontWeight: 500,
            }}>
              <span>✅</span> {t("login.resent")}
            </div>
          )}

          {/* Verify button */}
          <button
            data-testid="mfa-verify-button"
            type="submit" disabled={verifying || otp.length !== 6}
            style={{
              width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
              background: (verifying || otp.length !== 6)
                ? "#e5e7eb"
                : "linear-gradient(135deg, #0db87e 0%, #089962 100%)",
              color: (verifying || otp.length !== 6) ? "#9ca3af" : "#fff",
              fontFamily: "var(--fb)", fontSize: 15, fontWeight: 700,
              cursor: (verifying || otp.length !== 6) ? "not-allowed" : "pointer",
              boxShadow: (verifying || otp.length !== 6) ? "none" : "0 4px 18px rgba(13,184,126,.38)",
              transition: "all .2s", letterSpacing: ".2px",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              marginBottom: 14,
            }}
          >
            {verifying
              ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> {t("login.verifying")}</>
              : <>{t("login.verify")}</>
            }
          </button>

          {/* Resend + back */}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <button
              type="button" onClick={resendOtp} disabled={resending}
              style={{ background: "none", border: "none", color: "#0db87e", fontWeight: 600, cursor: resending ? "default" : "pointer", padding: 0 }}
            >
              {resending ? t("login.resending") : t("login.resend")}
            </button>
            <button
              type="button"
              onClick={() => { setMfaChallenge(null); setOtp(""); setError(null); }}
              style={{ background: "none", border: "none", color: "#6b7280", fontWeight: 600, cursor: "pointer", padding: 0 }}
            >
              {t("login.backToLogin")}
            </button>
          </div>

        </form>
        ) : (
        <form onSubmit={submit}>

          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, letterSpacing: ".5px", textTransform: "uppercase" }}>
              {t("login.email")}
            </label>
            <input
              type="email" value={email} required
              data-testid="email-input"
              onChange={e => setEmail(e.target.value)}
              onFocus={() => setEmailFocus(true)}
              onBlur={() => setEmailFocus(false)}
              placeholder="admin@kungabasics.com"
              style={inp(emailFocus)}
            />
          </div>

          {/* Password + eye */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, letterSpacing: ".5px", textTransform: "uppercase" }}>
              {t("login.password")}
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPw ? "text" : "password"} value={pw} required
                data-testid="password-input"
                onChange={e => setPw(e.target.value)}
                onFocus={() => setPwFocus(true)}
                onBlur={() => setPwFocus(false)}
                placeholder="••••••••"
                style={{ ...inp(pwFocus), paddingRight: 46 }}
              />
              <button
                type="button" tabIndex={-1}
                onClick={() => setShowPw(v => !v)}
                title={showPw ? "Hide password" : "Show password"}
                style={{
                  position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", padding: 4,
                  color: showPw ? "#0db87e" : "#9ca3af",
                  display: "flex", alignItems: "center", borderRadius: 6, transition: "color .15s",
                }}
              >
                {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div data-testid="login-error" style={{
              background: "#fef2f2", color: "#b91c1c",
              border: "1px solid #fca5a5", borderRadius: 10,
              padding: "10px 14px", fontSize: 13, marginBottom: 18,
              display: "flex", alignItems: "center", gap: 8, fontWeight: 500,
            }}>
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Sign in button */}
          <button
            data-testid="login-button"
            type="submit" disabled={loading}
            style={{
              width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
              background: loading
                ? "#e5e7eb"
                : "linear-gradient(135deg, #0db87e 0%, #089962 100%)",
              color: loading ? "#9ca3af" : "#fff",
              fontFamily: "var(--fb)", fontSize: 15, fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : "0 4px 18px rgba(13,184,126,.38)",
              transition: "all .2s", letterSpacing: ".2px",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {loading
              ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> {t("login.signingIn")}</>
              : <>{t("login.signIn")} →</>
            }
          </button>

        </form>
        )}

        {/* Card footer */}
        <div style={{
          marginTop: 22, paddingTop: 18,
          borderTop: "1px solid #f3f4f6",
          textAlign: "center", fontSize: 11, color: "#9ca3af", lineHeight: 1.7,
        }}>
          🔒 Admin access only · Unauthorised entry is prohibited
        </div>

      </div>
  );

  if (modal) return card;

  return (
    <div className="imigongo-bg" style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "32px 16px 24px",
      background: "#07120e",
      backgroundImage: "radial-gradient(ellipse 90% 55% at 50% 0%, rgba(13,184,126,.13) 0%, transparent 70%)",
    }}>
      {/* Brand above card */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 18, margin: "0 auto 14px",
          background: "#ffffff", padding: 8,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 26, boxShadow: "0 8px 32px rgba(13,184,126,.4)",
        }}><img src="/icon.png" style={{ width: 38, height: 38, objectFit: "contain" }} alt="Kunga Basics" /></div>
        <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 20, color: "#fff", letterSpacing: "-.3px" }}>
          Kunga Basics
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,.38)", marginTop: 4, letterSpacing: ".4px" }}>
          ADMIN PORTAL
        </div>
      </div>

      {card}

      <div style={{ marginTop: 28, fontSize: 11, color: "rgba(255,255,255,.2)", textAlign: "center", lineHeight: 1.8 }}>
        © {year} Kunga Basics Ltd. All rights reserved.<br />
        <span style={{ opacity: .7 }}>Privacy Policy · Terms of Service</span>
      </div>
    </div>
  );
}

// ─── APP FOOTER (shown on all authenticated pages) ────────────────────────────

function AppFooter({ onNavigate }) {
  const year  = new Date().getFullYear();

  const { t: tFooter } = useLang();
  const FOOTER_LINKS = [
    { label: tFooter("footer.privacy"), page: "privacy"  },
    { label: tFooter("footer.terms"),   page: "terms"    },
    { label: tFooter("footer.support"), page: "support"  },
  ];

  return (
    <footer style={{
      borderTop: "1px solid var(--border)",
      background: "var(--card)",
      padding: "12px 24px",
      display: "flex", alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap", gap: 10, flexShrink: 0,
    }}>

      {/* Left — brand + version */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img src="/icon.png" style={{ width: 18, height: 18, borderRadius: 4, objectFit: "cover" }} alt="Kunga Basics" />
        <span style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 12, color: "var(--ink)" }}>
          Kunga Basics
        </span>
        <span style={{ color: "var(--border)", fontSize: 12 }}>·</span>
        <span style={{ fontSize: 11, color: "var(--ghost)", fontFamily: "var(--fm)" }}>
          Admin Portal v1.0
        </span>
      </div>

      {/* Centre — copyright */}
      <div style={{ fontSize: 11, color: "var(--ghost)", textAlign: "center" }}>
        © {year} Kunga Basics Ltd. All rights reserved.
      </div>

      {/* Right — links */}
      <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
        {FOOTER_LINKS.map(({ label, page }) => (
          <span
            key={page}
            onClick={() => onNavigate?.(page)}
            style={{ fontSize: 11, color: "var(--ghost)", cursor: "pointer", transition: "color .15s" }}
            onMouseEnter={e => e.currentTarget.style.color = "var(--teal)"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--ghost)"}
          >
            {label}
          </span>
        ))}
      </div>

    </footer>
  );
}

// ─── NOTIFICATIONS PANEL ─────────────────────────────────────────────────────

const TYPE_BG    = { error: "var(--rose-lo)", warning: "var(--amber-lo)", success: "var(--teal-dim)", info: "var(--blue-lo)" };
const TYPE_COLOR = { error: "var(--rose)",    warning: "var(--amber)",    success: "var(--teal)",     info: "var(--blue)"   };

function NotifPanel({ notifs, loading, unreadCount, seenNotifs, onClose, onNavigate, onRefresh, onMarkOneSeen, onMarkAllRead }) {
  const { t } = useLang();
  return (
    <div className="notif-panel">
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: "var(--fd)", fontWeight: 700, fontSize: 14 }}>{t("notif.title")}</span>
          {unreadCount > 0 && (
            <span className="badge bg-red" style={{ fontSize: 10 }}>{unreadCount} {t("notif.new")}</span>
          )}
        </div>
        <button onClick={onRefresh} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)", display: "flex", padding: 2 }} title="Refresh">
          <RefreshCw size={12} />
        </button>
      </div>

      <div style={{ maxHeight: 360, overflowY: "auto" }}>
        {loading
          ? <div style={{ padding: 32, textAlign: "center", color: "var(--ghost)", fontSize: 12 }}>{t("common.loading")}</div>
          : notifs.length === 0
          ? (
            <div style={{ padding: 32, textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>{t("notif.allCaughtUp")}</div>
              <div style={{ fontSize: 11, color: "var(--ghost)" }}>{t("notif.noPending")}</div>
            </div>
          )
          : notifs.map(n => {
            const isRead = seenNotifs[n.id] === n.title;
            return (
              <div
                key={n.id}
                onClick={() => { onMarkOneSeen(n.id, n.title); onNavigate(n.link); onClose(); }}
                style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", cursor: "pointer", display: "flex", gap: 12, alignItems: "flex-start", transition: "background .15s", opacity: isRead ? 0.5 : 1 }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--teal-dim)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}
              >
                <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: TYPE_BG[n.type] ?? "var(--teal-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                  {n.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: isRead ? 400 : 600, color: isRead ? "var(--ghost)" : "var(--ink)", lineHeight: 1.4 }}>{n.title}</div>
                  <div style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                    {!isRead && <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: TYPE_COLOR[n.type] ?? "var(--teal)", flexShrink: 0 }} />}
                    {n.time}{!isRead && ` · ${t("notif.tapToView")}`}
                  </div>
                </div>
              </div>
            );
          })
        }
      </div>

      <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8 }}>
        <button className="btn btn-out btn-sm" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>{t("notif.close")}</button>
        {unreadCount > 0 && (
          <button className="btn btn-teal btn-sm" style={{ flex: 1, justifyContent: "center" }} onClick={onMarkAllRead}>
            {t("notif.markAllRead")}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── SMTP TEST CARD ───────────────────────────────────────────────────────────

function SmtpTestCard({ api: apiProp, t }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok: bool, msg: string }

  const send = async () => {
    if (!email.trim()) return;
    setSending(true); setResult(null);
    try {
      const r = await (apiProp ?? adminApi).testEmail(email.trim());
      setResult({ ok: true, msg: r.message ?? "Test email sent!" });
    } catch (e) {
      setResult({ ok: false, msg: e.message ?? "Failed to send test email" });
    } finally { setSending(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div className="card-hdr">
        <div className="card-ttl">
          <div className="card-ico">📧</div>
          {t("smtp.testTitle") || "SMTP Email Test"}
        </div>
      </div>
      <div className="card-body">
        <p style={{ fontSize: 13, color: "var(--ghost)", marginBottom: 16, lineHeight: 1.5 }}>
          {t("smtp.testDesc") || "Send a test email to verify your SMTP configuration is working correctly."}
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: .6 }}>
              {t("smtp.testRecipient") || "Recipient Email"}
            </label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setResult(null); }}
              placeholder="you@example.com"
              style={{ width: "100%", padding: "9px 12px", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", background: "var(--card)", color: "var(--ink)", boxSizing: "border-box" }}
              onKeyDown={e => e.key === "Enter" && send()}
            />
          </div>
          <button
            onClick={send}
            disabled={sending || !email.trim()}
            className="btn btn-primary"
            style={{ whiteSpace: "nowrap", opacity: sending || !email.trim() ? 0.6 : 1 }}
          >
            {sending ? "Sending…" : (t("smtp.sendTest") || "Send Test")}
          </button>
        </div>
        {result && (
          <div style={{
            marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500,
            background: result.ok ? "var(--teal-dim)" : "var(--rose-lo)",
            color: result.ok ? "var(--teal)" : "var(--rose)",
            border: `1px solid ${result.ok ? "var(--teal)" : "var(--rose)"}`,
          }}>
            {result.ok ? "✅ " : "❌ "}{result.msg}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ACCOUNT SETTINGS PAGE ───────────────────────────────────────────────────

function PageAccount({ me, onSignOut, toast, onNameChange, onAvatarChange, theme, onThemeChange, onMfaChange }) {
  const { t, lang, setLang } = useLang();
  const [tab, setTab]             = useState("profile");
  const [name, setName]           = useState(me?.name ?? "");
  const [savingName, setSaveName] = useState(false);
  const [curPw, setCurPw]         = useState("");
  const [newPw, setNewPw]         = useState("");
  const [confPw, setConfPw]       = useState("");
  const [savingPw, setSavePw]     = useState(false);
  const [signOutConfirm, setSignOutConfirm] = useState(false);
  // MFA state — initialized from me.mfaEnabled (DB value loaded at login)
  const [mfaEnabled, setMfaEnabled]   = useState(me?.mfaEnabled ?? false);
  const [mfaSetup, setMfaSetup]       = useState(false);
  const [otpSent, setOtpSent]         = useState(false);
  const [otpCode, setOtpCode]         = useState("");
  const [otpSending, setOtpSending]   = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [disableOtpSent, setDisableOtpSent]         = useState(false);
  const [disableOtpCode, setDisableOtpCode]         = useState("");
  const [disableOtpSending, setDisableOtpSending]   = useState(false);
  const [disableOtpVerifying, setDisableOtpVerifying] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarProgress, setAvatarProgress]   = useState(0);
  const avatarInputRef = useRef(null);

  const saveName = async () => {
    if (!name.trim()) { toast(t("acct.nameEmpty")); return; }
    setSaveName(true);
    try {
      const r = await authApi.updateMe({ name: name.trim() });
      onNameChange(r.user?.name ?? name.trim());
      toast(t("acct.nameSaved"));
    } catch (e) { toast("Error: " + e.message); }
    finally { setSaveName(false); }
  };

  const changePw = async () => {
    if (!curPw || !newPw || !confPw) { toast(t("acct.allFieldsRequired")); return; }
    if (newPw !== confPw) { toast(t("acct.pwNoMatch")); return; }
    if (newPw.length < 8) { toast(t("acct.pwTooShort")); return; }
    setSavePw(true);
    try {
      await authApi.changePassword({ currentPassword: curPw, newPassword: newPw });
      toast(t("acct.pwChanged")); setCurPw(""); setNewPw(""); setConfPw("");
    } catch (e) { toast("Error: " + e.message); }
    finally { setSavePw(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast("Please select an image file"); return; }
    setAvatarUploading(true); setAvatarProgress(0);
    try {
      const r = await authApi.uploadAvatar(file, setAvatarProgress);
      onAvatarChange(r.avatarUrl);
      toast("✅ Avatar updated!");
    } catch (err) { toast("Error: " + err.message); }
    finally { setAvatarUploading(false); setAvatarProgress(0); if (avatarInputRef.current) avatarInputRef.current.value = ""; }
  };

  const initials = (me?.name ?? "A").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>

      {/* ── Header strip ─────────────────────────────────────── */}
      <div style={{ background: "var(--panel-dark)", borderRadius: 16, padding: "20px 24px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, flexShrink: 0, overflow: "hidden" }}>
          {me?.avatarUrl
            ? <img src={me.avatarUrl} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            : <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, var(--teal) 0%, #086b47 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--fd)", fontWeight: 900, fontSize: 18, color: "white" }}>{initials}</div>
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 17, color: "white", lineHeight: 1.2 }}>{me?.name ?? "Admin"}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.45)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{me?.email}</div>
        </div>
        <span style={{ background: "rgba(13,184,126,.2)", color: "var(--teal)", fontSize: 10, fontWeight: 700, padding: "4px 12px", borderRadius: 20, textTransform: "uppercase", letterSpacing: ".8px", flexShrink: 0 }}>
          {me?.role ?? "ADMIN"}
        </span>
      </div>

      {/* ── Two-column layout ─────────────────────────────────── */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

        {/* Left nav */}
        <div style={{ width: 180, flexShrink: 0 }}>
          <div style={{ background: "var(--card)", borderRadius: 14, border: "1px solid var(--border)", overflow: "hidden", boxShadow: "var(--sh)" }}>
            {ACCT_TABS_CFG.map(tabCfg => (
              <button
                key={tabCfg.id}
                onClick={() => setTab(tabCfg.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "12px 16px", border: "none", background: tab === tabCfg.id ? "var(--teal-dim)" : "transparent",
                  borderLeft: `3px solid ${tab === tabCfg.id ? "var(--teal)" : "transparent"}`,
                  cursor: "pointer", fontFamily: "var(--fb)", fontSize: 13,
                  fontWeight: tab === tabCfg.id ? 700 : 500,
                  color: tab === tabCfg.id ? "var(--teal)" : "var(--muted)",
                  transition: "all .15s",
                }}
              >
                <tabCfg.icon size={14} />
                {t(tabCfg.labelKey)}
              </button>
            ))}
            <div style={{ height: 1, background: "var(--border)" }} />
            <button
              onClick={() => setSignOutConfirm(true)}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                padding: "12px 16px", border: "none", background: "transparent",
                borderLeft: "3px solid transparent", cursor: "pointer",
                fontFamily: "var(--fb)", fontSize: 13, fontWeight: 500,
                color: "var(--rose)", transition: "all .15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--rose-lo)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <LogOut size={14} /> {t("common.signOut")}
            </button>
          </div>
        </div>

        {/* Right panel */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* ── Profile tab ──────────────────────────────────── */}
          {tab === "profile" && (
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="card-hdr">
                <div className="card-ttl"><div className="card-ico"><UserCircle size={14} /></div>{t("acct.profileInfo")}</div>
              </div>
              <div className="card-body">
                <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={uploadAvatar} />
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 20 }}>
                  <div
                    className={`av-wrap${avatarUploading ? " av-uploading" : ""}`}
                    onClick={() => !avatarUploading && avatarInputRef.current?.click()}
                    title={t("acct.changePhoto")}
                    style={{ width: 64, height: 64, flexShrink: 0 }}
                  >
                    {me?.avatarUrl
                      ? <img src={me.avatarUrl} alt="avatar" style={{ width: 64, height: 64, borderRadius: 16, objectFit: "cover", display: "block" }} />
                      : <div style={{ width: 64, height: 64, borderRadius: 16, background: "linear-gradient(135deg, var(--teal), #086b47)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--fd)", fontWeight: 900, fontSize: 22, color: "white" }}>{initials}</div>
                    }
                    <div className="av-overlay">
                      {avatarUploading
                        ? <span style={{ fontSize: 11, color: "white", fontWeight: 700 }}>{avatarProgress}%</span>
                        : <Camera size={18} color="white" />
                      }
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>{me?.name ?? "Admin"}</div>
                    <div style={{ fontSize: 12, color: "var(--ghost)", marginTop: 2 }}>{t("acct.adminRole")}</div>
                    <button
                      className="btn btn-out btn-xs"
                      style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 5 }}
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={avatarUploading}
                    >
                      <Camera size={10} /> {avatarUploading ? t("acct.uploading", { pct: avatarProgress }) : t("acct.changePhoto")}
                    </button>
                    <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 6, fontFamily: "var(--fm)", background: "var(--surface)", padding: "3px 8px", borderRadius: 6, display: "inline-block", border: "1px solid var(--border)" }}>
                      {me?.email}
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                  <div className="f-grp">
                    <label className="f-lbl">{t("acct.displayName")}</label>
                    <input className="f-in" value={name} onChange={e => setName(e.target.value)} placeholder={t("acct.displayName")} style={{ maxWidth: 320 }} />
                  </div>
                  <div className="f-grp">
                    <label className="f-lbl">{t("acct.emailAddress")}</label>
                    <input className="f-in" value={me?.email ?? ""} disabled style={{ opacity: .55, cursor: "not-allowed", maxWidth: 320 }} />
                    <div style={{ fontSize: 11, color: "var(--ghost)", marginTop: 4 }}>{t("acct.emailHint")}</div>
                  </div>
                  <button
                    className="btn btn-teal"
                    onClick={saveName}
                    disabled={savingName || name.trim() === (me?.name ?? "")}
                    style={{ marginTop: 4 }}
                  >
                    {savingName ? t("acct.saving") : t("acct.saveChanges")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Security tab ─────────────────────────────────── */}
          {tab === "security" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Change Password */}
              <div className="card" style={{ marginBottom: 0 }}>
                <div className="card-hdr">
                  <div className="card-ttl"><div className="card-ico"><Lock size={14} /></div>{t("acct.changePassword")}</div>
                </div>
                <div className="card-body">
                  <div style={{ background: "var(--amber-lo)", border: "1px solid var(--amber)", borderRadius: 10, padding: "10px 14px", marginBottom: 18, fontSize: 12, color: "#92400e", lineHeight: 1.5 }}>
                    {t("acct.pwHint")}
                  </div>
                  <div className="f-grp">
                    <label className="f-lbl">{t("acct.currentPw")}</label>
                    <input className="f-in" type="password" value={curPw} onChange={e => setCurPw(e.target.value)} placeholder={t("acct.currentPwPlaceholder")} style={{ maxWidth: 340 }} />
                  </div>
                  <div style={{ borderTop: "1px solid var(--border)", margin: "14px 0" }} />
                  <div className="f-grp">
                    <label className="f-lbl">{t("acct.newPw")}</label>
                    <input className="f-in" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder={t("acct.newPwPlaceholder")} style={{ maxWidth: 340 }} />
                  </div>
                  <div className="f-grp">
                    <label className="f-lbl">{t("acct.confirmPw")}</label>
                    <input className="f-in" type="password" value={confPw} onChange={e => setConfPw(e.target.value)} placeholder={t("acct.confirmPwPlaceholder")}
                      style={{ maxWidth: 340, borderColor: confPw && confPw !== newPw ? "var(--rose)" : "" }}
                    />
                    {confPw && confPw !== newPw && <div style={{ fontSize: 11, color: "var(--rose)", marginTop: 4 }}>{t("acct.pwMismatch")}</div>}
                  </div>
                  <button
                    className="btn btn-teal"
                    onClick={changePw}
                    disabled={savingPw || !curPw || !newPw || !confPw || newPw !== confPw}
                    style={{ marginTop: 4 }}
                  >
                    {savingPw ? t("acct.updating") : t("acct.updatePw")}
                  </button>
                </div>
              </div>

              {/* ── Two-Factor Authentication ─────────────────── */}
              <div className="card" style={{ marginBottom: 0 }}>
                <div className="card-hdr">
                  <div className="card-ttl"><div className="card-ico">🔐</div>{t("acct.mfa")}</div>
                  {mfaEnabled && (
                    <span style={{ background: "var(--teal-dim)", color: "var(--teal)", fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>
                      ✓ ON
                    </span>
                  )}
                </div>
                <div className="card-body">
                  <div style={{ fontSize: 12, color: "var(--ghost)", lineHeight: 1.6, marginBottom: 16 }}>
                    {t("acct.mfaHint")}
                  </div>

                  {/* Status banner */}
                  <div style={{ padding: "10px 14px", borderRadius: 10, marginBottom: 16, fontSize: 12, fontWeight: 600,
                    background: mfaEnabled ? "var(--teal-dim)" : "var(--surface)",
                    border: `1px solid ${mfaEnabled ? "var(--teal-lo)" : "var(--border)"}`,
                    color: mfaEnabled ? "var(--teal)" : "var(--ghost)", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{mfaEnabled ? "🔐" : "🔓"}</span>
                    {mfaEnabled ? t("acct.mfaEnabled") : t("acct.mfaDisabled")}
                  </div>

                  {mfaEnabled ? (
                    /* Disable MFA — requires OTP confirmation */
                    !disableOtpSent ? (
                      <button
                        className="btn btn-out"
                        style={{ color: "var(--rose)", borderColor: "var(--rose)" }}
                        disabled={disableOtpSending}
                        onClick={async () => {
                          setDisableOtpSending(true);
                          try {
                            const r = await authApi.sendMfaDisable();
                            setDisableOtpSent(true);
                            toast(t("acct.otpSent", { dest: r?.maskedEmail ?? me?.email ?? "" }));
                          } catch (e) {
                            toast("Error: " + e.message);
                          } finally {
                            setDisableOtpSending(false);
                          }
                        }}
                      >
                        {disableOtpSending ? t("acct.sendingOtp") : t("acct.disableMfa")}
                      </button>
                    ) : (
                      <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 16, background: "var(--surface)" }}>
                        <div style={{ padding: "8px 12px", background: "var(--card)", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 14, fontSize: 12, color: "var(--ink2)" }}>
                          {`📧 ${me?.email ?? ""}`}
                        </div>
                        <div className="f-grp">
                          <label className="f-lbl">{t("acct.otpCode")}</label>
                          <input
                            className="f-in"
                            value={disableOtpCode}
                            onChange={e => setDisableOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder={t("acct.otpPlaceholder")}
                            style={{ fontFamily: "var(--fm)", fontSize: 20, letterSpacing: 8, maxWidth: 200, textAlign: "center" }}
                            autoFocus
                            maxLength={6}
                          />
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                          <button className="btn btn-out" onClick={() => { setDisableOtpSent(false); setDisableOtpCode(""); }}>
                            {t("common.cancel")}
                          </button>
                          <button
                            className="btn btn-out"
                            style={{ flex: 1, justifyContent: "center", color: "var(--rose)", borderColor: "var(--rose)" }}
                            disabled={disableOtpVerifying || disableOtpCode.length !== 6}
                            onClick={async () => {
                              setDisableOtpVerifying(true);
                              try {
                                await authApi.verifyMfaDisable(disableOtpCode);
                                setMfaEnabled(false);
                                setMfaSetup(false);
                                setOtpSent(false);
                                setOtpCode("");
                                setDisableOtpSent(false);
                                setDisableOtpCode("");
                                onMfaChange?.(false);
                                toast(t("acct.mfaDisabledToast"));
                              } catch (e) {
                                toast("❌ " + e.message);
                              } finally {
                                setDisableOtpVerifying(false);
                              }
                            }}
                          >
                            {disableOtpVerifying ? t("acct.verifyingOtp") : t("acct.verifyOtp")}
                          </button>
                        </div>
                        {/* Resend */}
                        <div style={{ textAlign: "center", marginTop: 10 }}>
                          <button
                            onClick={async () => {
                              setDisableOtpCode("");
                              try {
                                const r = await authApi.sendMfaDisable();
                                toast(t("acct.otpSent", { dest: r?.maskedEmail ?? me?.email ?? "" }));
                              } catch (e) {
                                toast("Error: " + e.message);
                              }
                            }}
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--teal)", textDecoration: "underline" }}
                          >
                            {t("acct.sendOtp")} →
                          </button>
                        </div>
                      </div>
                    )
                  ) : !mfaSetup ? (
                    /* Enable button */
                    <button className="btn btn-teal" onClick={() => setMfaSetup(true)}>
                      🔐 {t("acct.enableMfa")}
                    </button>
                  ) : (
                    /* Setup flow */
                    <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 16, background: "var(--surface)" }}>
                      {/* Destination display */}
                      <div style={{ padding: "8px 12px", background: "var(--card)", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 14, fontSize: 12, color: "var(--ink2)" }}>
                        {`📧 ${me?.email ?? ""}`}
                      </div>

                      {/* Send code button */}
                      {!otpSent && (
                        <button
                          className="btn btn-teal"
                          style={{ width: "100%", justifyContent: "center", marginBottom: 14 }}
                          disabled={otpSending}
                          onClick={async () => {
                            setOtpSending(true);
                            try {
                              const r = await authApi.sendMfaSetup();
                              setOtpSent(true);
                              toast(t("acct.otpSent", { dest: r?.maskedEmail ?? me?.email ?? "" }));
                            } catch (e) {
                              toast("Error: " + e.message);
                            } finally {
                              setOtpSending(false);
                            }
                          }}
                        >
                          {otpSending ? t("acct.sendingOtp") : t("acct.sendOtp")}
                        </button>
                      )}

                      {/* OTP code input */}
                      {otpSent && (
                        <div>
                          <div className="f-grp">
                            <label className="f-lbl">{t("acct.otpCode")}</label>
                            <input
                              className="f-in"
                              value={otpCode}
                              onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                              placeholder={t("acct.otpPlaceholder")}
                              style={{ fontFamily: "var(--fm)", fontSize: 20, letterSpacing: 8, maxWidth: 200, textAlign: "center" }}
                              autoFocus
                              maxLength={6}
                            />
                          </div>
                          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                            <button className="btn btn-out" onClick={() => { setMfaSetup(false); setOtpSent(false); setOtpCode(""); }}>
                              {t("common.cancel")}
                            </button>
                            <button
                              className="btn btn-teal"
                              style={{ flex: 1, justifyContent: "center" }}
                              disabled={otpVerifying || otpCode.length !== 6}
                              onClick={async () => {
                                setOtpVerifying(true);
                                try {
                                  await authApi.verifyMfaSetup(otpCode);
                                  setMfaEnabled(true);
                                  setMfaSetup(false);
                                  setOtpSent(false);
                                  setOtpCode("");
                                  onMfaChange?.(true);
                                  toast(t("acct.mfaEnabledToast"));
                                } catch (e) {
                                  toast("❌ " + e.message);
                                } finally {
                                  setOtpVerifying(false);
                                }
                              }}
                            >
                              {otpVerifying ? t("acct.verifyingOtp") : t("acct.verifyOtp")}
                            </button>
                          </div>
                          {/* Resend */}
                          <div style={{ textAlign: "center", marginTop: 10 }}>
                            <button
                              onClick={async () => {
                                setOtpCode("");
                                try {
                                  const r = await authApi.sendMfaSetup();
                                  toast(t("acct.otpSent", { dest: r?.maskedEmail ?? me?.email ?? "" }));
                                } catch (e) {
                                  toast("Error: " + e.message);
                                }
                              }}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--teal)", textDecoration: "underline" }}
                            >
                              {t("acct.sendOtp")} →
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Appearance tab ───────────────────────────────── */}
          {tab === "appearance" && (
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="card-hdr">
                <div className="card-ttl"><div className="card-ico"><Palette size={14} /></div>{t("acct.appearance")}</div>
              </div>
              <div className="card-body">
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 4 }}>{t("acct.colorTheme")}</div>
                  <div style={{ fontSize: 12, color: "var(--ghost)", marginBottom: 14 }}>{t("acct.themeHint")}</div>
                  <div className="theme-pill">
                    {[
                      { id: "light",  labelKey: "acct.light",  icon: <Sun  size={13} /> },
                      { id: "dark",   labelKey: "acct.dark",   icon: <Moon size={13} /> },
                      { id: "system", labelKey: "acct.system", icon: <Monitor size={13} /> },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        className={`theme-opt${theme === opt.id ? " active" : ""}`}
                        onClick={() => onThemeChange(opt.id)}
                      >
                        {opt.icon} {t(opt.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ borderTop: "1px solid var(--border)", paddingTop: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".7px", marginBottom: 12 }}>{t("acct.preview")}</div>
                  <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, display: "flex", gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--teal)", marginTop: 2, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 4 }}>{t("acct.sampleCard")}</div>
                      <div style={{ fontSize: 11, color: "var(--ghost)" }}>{t("acct.sampleCardHint")}</div>
                      <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                        <span style={{ background: "var(--teal-dim)", color: "var(--teal)", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>{t("acct.active")}</span>
                        <span style={{ background: "var(--amber-lo)", color: "var(--amber)", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>{t("acct.pending")}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Language tab ─────────────────────────────────── */}
          {tab === "language" && (
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="card-hdr">
                <div className="card-ttl"><div className="card-ico"><Globe size={14} /></div>{t("acct.languageTitle")}</div>
              </div>
              <div className="card-body">
                <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.6, marginBottom: 20 }}>
                  {t("acct.languageHint")}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {LANGUAGES.map(l => {
                    const isActive = lang === l.code;
                    return (
                      <button
                        key={l.code}
                        onClick={() => { setLang(l.code); toast(t("acct.languageSaved")); authApi.updateMe({ lang: l.code }).catch(() => {}); }}
                        style={{
                          display: "flex", alignItems: "center", gap: 16,
                          padding: "16px 20px", border: "none", borderRadius: 14, cursor: "pointer",
                          textAlign: "left", width: "100%", transition: "all .15s",
                          background: isActive ? "var(--teal-dim)" : "var(--surface)",
                          outline: `2px solid ${isActive ? "var(--teal)" : "var(--border)"}`,
                          outlineOffset: 0,
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.outline = "2px solid var(--teal)"; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.outline = "2px solid var(--border)"; }}
                      >
                        {/* Flag */}
                        <span style={{ fontSize: 32, lineHeight: 1, flexShrink: 0 }}>{l.flag}</span>

                        {/* Names */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 15, fontWeight: 700, fontFamily: "var(--fd)",
                            color: isActive ? "var(--teal)" : "var(--ink)",
                          }}>
                            {l.nativeName}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--ghost)", marginTop: 2 }}>
                            {l.enName}
                          </div>
                        </div>

                        {/* Active checkmark */}
                        {isActive && (
                          <div style={{
                            width: 24, height: 24, borderRadius: "50%",
                            background: "var(--teal)", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}>
                            <span style={{ color: "white", fontSize: 13, fontWeight: 900 }}>✓</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Current language badge */}
                <div style={{ marginTop: 20, padding: "10px 14px", background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
                  <Globe size={14} color="var(--ghost)" />
                  <span style={{ fontSize: 12, color: "var(--ghost)" }}>{t("acct.langCurrent")}:</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "var(--teal)" }}>
                    {LANGUAGES.find(l => l.code === lang)?.nativeName ?? "English"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── Session tab ──────────────────────────────────── */}
          {tab === "session" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="card" style={{ marginBottom: 0 }}>
                <div className="card-hdr">
                  <div className="card-ttl"><div className="card-ico"><Settings size={14} /></div>{t("acct.sessionAccess")}</div>
                </div>
                <div className="card-body">
                  <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    {[
                      [t("acct.role"),       me?.role ?? "ADMIN",      t("acct.roleHint")      ],
                      [t("acct.access"),     t("acct.fullAdmin"),       t("acct.accessHint")    ],
                      [t("acct.authMethod"), t("acct.jwtBearer"),       t("acct.authMethodHint")],
                      [t("acct.status"),     t("acct.statusActive"),    t("acct.statusHint")    ],
                    ].map(([k, v, hint]) => (
                      <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 0", borderBottom: "1px solid var(--border)" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{k}</div>
                          <div style={{ fontSize: 11, color: "var(--ghost)", marginTop: 2 }}>{hint}</div>
                        </div>
                        <div style={{ fontFamily: "var(--fm)", fontSize: 12, fontWeight: 700, color: v === t("acct.statusActive") ? "var(--teal)" : "var(--ink2)", background: "var(--surface)", padding: "4px 12px", borderRadius: 8, border: "1px solid var(--border)" }}>
                          {v}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── SMTP Test Card ── */}
              <SmtpTestCard t={t} />
            </div>
          )}
        </div>
      </div>

      {/* ── Sign-out confirmation modal ── */}
      {signOutConfirm && (
        <div className="modal-ov open">
          <div className="modal" style={{ maxWidth: 380 }}>
            <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--rose-lo)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: 22 }}>
                <LogOut size={22} color="var(--rose)" />
              </div>
              <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 17, marginBottom: 8 }}>{t("signout.title")}</div>
              <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>{t("signout.message")}</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-out" style={{ flex: 1, justifyContent: "center" }} onClick={() => setSignOutConfirm(false)}>{t("signout.cancel")}</button>
              <button className="btn btn-danger" style={{ flex: 1, justifyContent: "center" }} onClick={onSignOut}>{t("signout.confirm")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SCROLLABLE PUBLIC PAGE WRAPPER ──────────────────────────────────────────
// Used for Privacy Policy and Terms pages served as public routes.
// STYLES sets overflow:hidden on html/body/#root for the admin layout — this
// component overrides that so the page can scroll normally.

function ScrollablePage({ children }) {
  useEffect(() => {
    const els = [document.documentElement, document.body];
    const root = document.getElementById("root");
    const prev = els.map(el => el.style.overflow);
    const prevH = els.map(el => el.style.height);
    const prevRoot = root?.style.overflow;
    const prevRootH = root?.style.height;
    els.forEach(el => { el.style.overflow = "auto"; el.style.height = "auto"; });
    if (root) { root.style.overflow = "auto"; root.style.height = "auto"; }
    return () => {
      els.forEach((el, i) => { el.style.overflow = prev[i]; el.style.height = prevH[i]; });
      if (root) { root.style.overflow = prevRoot; root.style.height = prevRootH; }
    };
  }, []);
  return <><style>{STYLES}</style>{children}</>;
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────

export default function App() {
  const { t, setLang } = useLang();

  const [authed, setAuthed]             = useState(!!getToken());
  const [me, setMe]                     = useState(null);
  const [theme, setTheme]               = useTheme();
  const [page, setPage]                 = useState("dashboard");
  const [pageParam, setPageParam]       = useState(null);
  const [sidebarOpen, setSidebar]       = useState(false);
  const [sidebarCollapsed, setCollapsed] = useState(() => localStorage.getItem("kb_sb_collapsed") === "1");
  const [notifOpen, setNotifOpen]       = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [manageNav, setManageNav]       = useState(false);
  const [hiddenNav, setHiddenNav]       = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("kb_hidden_nav") ?? "[]")); }
    catch { return new Set(); }
  });

  const myPermissions = useMemo(() => new Set(me?.effectivePermissions ?? []), [me]);
  const hasPermission = (codes) => {
    if (!codes) return true;
    const list = Array.isArray(codes) ? codes : [codes];
    return list.some(c => myPermissions.has(c));
  };

  // Track an initial pageview once the admin is authenticated (covers page refresh / direct load)
  useEffect(() => {
    if (authed) trackPageview(`/${page}${pageParam ? `/${pageParam}` : ""}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // Notifications
  const { data: notifData, loading: notifLoading, reload: reloadNotifs } = useApi(
    () => authed ? adminApi.notifications() : Promise.resolve(null)
  );
  const notifs = notifData?.notifications ?? [];

  const [seenNotifs, setSeenNotifs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kb_seen_notifs") ?? "{}"); }
    catch { return {}; }
  });
  const markOneSeen = (id, title) => {
    setSeenNotifs(prev => {
      const next = { ...prev, [id]: title };
      localStorage.setItem("kb_seen_notifs", JSON.stringify(next));
      return next;
    });
  };
  const markAllSeen = (currentNotifs) => {
    const next = {};
    currentNotifs.forEach(n => { next[n.id] = n.title; });
    setSeenNotifs(next);
    localStorage.setItem("kb_seen_notifs", JSON.stringify(next));
  };
  const unreadCount = notifs.filter(n => seenNotifs[n.id] !== n.title).length;

  // Load current user on mount — apply DB preferences (theme, lang, mfaEnabled)
  useEffect(() => {
    if (!authed) return;
    authApi.me().then(r => {
      if (!r?.user) return;
      setMe(r.user);
      // Apply stored preferences from DB
      if (r.user.theme) setTheme(r.user.theme);
      if (r.user.lang)  setLang(r.user.lang);
      // Sync localStorage MFA flag from DB so gate works on next reload
      if (r.user.mfaEnabled) localStorage.setItem("kb_mfa_enabled", "1");
      else                    localStorage.removeItem("kb_mfa_enabled");
    }).catch(() => {});
  }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll notifications every 2 minutes
  useEffect(() => {
    if (!authed) return;
    const id = setInterval(reloadNotifs, 120_000);
    return () => clearInterval(id);
  }, [authed, reloadNotifs]);

  const toggleCollapse = () => setCollapsed(v => {
    const next = !v;
    localStorage.setItem("kb_sb_collapsed", next ? "1" : "0");
    return next;
  });
  const { toast, show: showToast } = useToast();
  const notifRef = useRef(null);

  // Close notif panel on outside click
  useEffect(() => {
    const h = e => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Session expiry — show toast then go back to login
  useEffect(() => {
    const h = () => {
      showToast(t("session.expired"));
      setTimeout(() => { setAuthed(false); setMe(null); }, 2500);
    };
    window.addEventListener("session:expired", h);
    return () => window.removeEventListener("session:expired", h);
  }, [showToast, t]);

  // ── Public routes — checked BEFORE auth gate (no login required) ─────────────
  const _searchParams  = new URLSearchParams(window.location.search);
  const _resetToken    = _searchParams.get("token");
  const _pathname      = window.location.pathname;

  if (_pathname.includes("reset-password") && _resetToken) return (
    <><style>{STYLES}</style><ResetPassword
      token={_resetToken}
      onBack={() => {
        // Clear any cached admin session so this device's existing login
        // isn't silently resumed after a different account's reset.
        setToken(null);
        window.history.replaceState({}, document.title, "/");
        window.location.reload();
      }}
    /></>
  );

  // Privacy Policy & Terms of Service — publicly accessible for mobile in-app WebView
  if (_pathname.includes("privacy")) return <ScrollablePage><PagePrivacyPolicy /></ScrollablePage>;
  if (_pathname.includes("terms"))   return <ScrollablePage><PageTermsOfService /></ScrollablePage>;

  if (!authed) {
    const handleLogin = (u) => {
      setMe(u);
      setAuthed(true);
      if (u.theme) setTheme(u.theme);
      if (u.lang)  setLang(u.lang);
      if (u.mfaEnabled) localStorage.setItem("kb_mfa_enabled", "1");
      else               localStorage.removeItem("kb_mfa_enabled");
      trackEvent('login', 'login', u?.email);
    };

    return (
      <>
        <style>{STYLES}</style>
        <Login onLogin={handleLogin} />
      </>
    );
  }

  const navigate = (id, param = null) => {
    setPage(id);
    setPageParam(param);
    setSidebar(false);
    setGlobalSearch("");
    setManageNav(false);
    trackPageview(`/${id}${param ? `/${param}` : ""}`);
  };

  const toggleHiddenNav = (id) => {
    setHiddenNav(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem("kb_hidden_nav", JSON.stringify([...next]));
      return next;
    });
  };

  // Save theme preference to DB then apply locally
  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    authApi.updateMe({ theme: newTheme }).catch(() => {});
  };

  const signOut = () => { trackEvent('logout', 'logout', me?.email); setToken(null); setAuthed(false); };

  // Translated breadcrumbs + search placeholders
  const CRUMBS = {
    dashboard:     t("crumb.dashboard"),
    analytics:     t("crumb.analytics"),
    visitors:      t("crumb.visitors"),
    modules:       t("crumb.modules"),
    moduleDetail:  t("crumb.moduleDetail"),
    videos:        t("crumb.videos"),
    announcements: t("crumb.announcements"),
    users:         t("crumb.users"),
    userDetail:    t("crumb.userDetail"),
    subscriptions: t("crumb.subscriptions"),
    askgad:        t("crumb.askgad"),
    donations:      t("crumb.donations"),
    mobilemoney:    t("crumb.mobilemoney"),
    cardpay:        t("crumb.cardpay"),
    manualpayments: "Manual Payments",
    transactions:  "Payment Transactions",
    pricing:       t("crumb.pricing"),
    account:       t("crumb.account"),
    privacy:       t("crumb.privacy"),
    terms:         t("crumb.terms"),
    support:       t("crumb.support"),
  };

  const SEARCH_PLACEHOLDER = {
    dashboard:     t("search.dashboard"),
    analytics:     t("search.analytics"),
    visitors:      t("search.visitors"),
    modules:       t("search.modules"),
    videos:        t("search.videos"),
    announcements: t("search.announcements"),
    users:         t("search.users"),
    subscriptions: t("search.subscriptions"),
    askgad:        t("search.askgad"),
    donations:     t("search.donations"),
    moduleDetail:  t("search.moduleDetail"),
    mobilemoney:    t("search.mobilemoney"),
    cardpay:        t("search.cardpay"),
    manualpayments: "Search by name, email or reference…",
    transactions:  "Search by name or email…",
    pricing:       t("search.pricing"),
    account:       t("search.account"),
    privacy:       t("search.privacy"),
    terms:         t("search.terms"),
    support:       t("search.support"),
  };

  const PAGES = {
    dashboard:     <PageDashboard onNavigate={navigate} toast={showToast} />,
    analytics:     <PageAnalytics />,
    visitors:      <PageVisitorAnalytics />,
    modules:       <PageModules      toast={showToast} search={globalSearch} onNavigate={navigate} hasPermission={hasPermission} />,
    moduleDetail:  pageParam ? <PageModuleDetail moduleId={pageParam} toast={showToast} onNavigate={navigate} search={globalSearch} /> : null,
    videos:        <PageVideos       toast={showToast} search={globalSearch} onNavigate={navigate} hasPermission={hasPermission} />,
    announcements: <PageAnnouncements toast={showToast} hasPermission={hasPermission} />,
    users:         <PageUsers        toast={showToast} search={globalSearch} onNavigate={navigate} />,
    userDetail:    pageParam ? <PageUserDetail userId={pageParam} toast={showToast} onNavigate={navigate} hasPermission={hasPermission} /> : null,
    subscriptions: <PageSubscriptions toast={showToast} search={globalSearch} hasPermission={hasPermission} />,
    askgad:        <PageAskGad       toast={showToast} search={globalSearch} hasPermission={hasPermission} me={me} />,
    donations:      <PageDonations      toast={showToast} />,
    mobilemoney:    <PageMobileMoney    toast={showToast} hasPermission={hasPermission} />,
    cardpay:        <PageCardPayments   toast={showToast} />,
    manualpayments: <PageManualPayments toast={showToast} hasPermission={hasPermission} />,
    transactions:  <PageTransactions toast={showToast} search={globalSearch} />,
    pricing:       <PagePricing      toast={showToast} hasPermission={hasPermission} />,
    supportteam:   <PageSupportTeam  toast={showToast} search={globalSearch} hasPermission={hasPermission} />,
    roles:         <PageRoles        toast={showToast} search={globalSearch} />,
    auditlog:      <PageAuditLog     toast={showToast} />,
    countries:     <PageCountryAvailability toast={showToast} hasPermission={hasPermission} />,
    assessments:   <PageAssessments  toast={showToast} search={globalSearch} />,
    account:       <PageAccount me={me} onSignOut={signOut} toast={showToast} onNameChange={n => setMe(m => ({ ...m, name: n }))} onAvatarChange={url => setMe(m => ({ ...m, avatarUrl: url }))} theme={theme} onThemeChange={handleThemeChange} onMfaChange={enabled => { setMe(m => ({ ...m, mfaEnabled: enabled })); if (enabled) localStorage.setItem("kb_mfa_enabled", "1"); else localStorage.removeItem("kb_mfa_enabled"); }} />,
    privacy:       <PagePrivacyPolicy />,
    terms:         <PageTermsOfService />,
    support:       <PageSupport />,
  };

  return (
    <>
      <style>{STYLES}</style>

      <div className="layout">
        {/* Mobile overlay */}
        <div className={`overlay${sidebarOpen ? " show" : ""}`} onClick={() => setSidebar(false)} />

        {/* ── Sidebar ─────────────────────────────────────────── */}
        <nav className={`sidebar${sidebarOpen ? " open" : ""}${sidebarCollapsed ? " collapsed" : ""}`}>
          <div className="sb-logo">
            <div className="sb-mark"><img src="/icon.png" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} alt="Kunga Basics" /></div>
            <div className="sb-head">
              <div className="sb-name">Kunga Basics</div>
              <div className="sb-ver">{t("login.subtitle")}</div>
            </div>
            <button className="sb-collapse-btn" onClick={toggleCollapse} title={sidebarCollapsed ? t("sidebar.expand") : t("sidebar.collapse")}>
              {sidebarCollapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
            </button>
          </div>

          {NAV_CFG.map(group => {
            const permitted = group.items.filter(i => hasPermission(i.permission));
            const visible = permitted.filter(i => !hiddenNav.has(i.id) || manageNav);
            if (visible.length === 0) return null;
            return (
              <div key={group.sectionKey}>
                <div className="sb-section">{t(group.sectionKey)}</div>
                {visible.map(item => {
                  const hidden = hiddenNav.has(item.id);
                  return (
                    <div key={item.id} style={{ display: "flex", alignItems: "center", position: "relative" }}>
                      <button
                        data-testid={`nav-${item.id}`}
                        className={`sb-item${page === item.id ? " active" : ""}${hidden ? " sb-item-hidden" : ""}`}
                        style={{ flex: 1, opacity: hidden ? 0.35 : 1 }}
                        onClick={() => !hidden && navigate(item.id)}
                      >
                        <div className="sb-icon"><item.icon size={15} color="rgba(255,255,255,.7)" /></div>
                        <span className="sb-lbl">{t(item.labelKey)}</span>
                      </button>
                      {manageNav && (
                        <button
                          onClick={() => toggleHiddenNav(item.id)}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: "0 10px 0 0", color: hidden ? "rgba(255,255,255,.25)" : "rgba(255,255,255,.6)", flexShrink: 0 }}
                          title={hidden ? t("sidebar.showTab") : t("sidebar.hideTab")}
                        >
                          {hidden ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div className="sb-foot">
            <div
              className="sb-av"
              style={{ cursor: "pointer", overflow: "hidden", width: 34, height: 34, background: me?.avatarUrl ? "transparent" : "linear-gradient(135deg, var(--teal), #086b47)" }}
              onClick={() => navigate("account")}
              title={t("topbar.accountSettings")}
            >
              {me?.avatarUrl
                ? <img src={me.avatarUrl} alt="avatar" style={{ width: 34, height: 34, objectFit: "cover", display: "block" }} />
                : <span style={{ fontSize: 13, fontWeight: 700, color: "white", fontFamily: "var(--fd)" }}>
                    {(me?.name ?? "A").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                  </span>
              }
            </div>
            <div className="sb-foot-info" onClick={() => navigate("account")}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>{me?.name ?? "Admin"}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{me?.email}</div>
            </div>
            <button
              className="sb-settings-btn"
              style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, padding: 4 }}
              onClick={() => setManageNav(v => !v)}
              title={t("topbar.manageSidebar")}
            >
              <Settings size={14} color={manageNav ? "var(--teal)" : "rgba(255,255,255,.3)"} />
            </button>
          </div>
          {manageNav && (
            <div style={{ padding: "8px 16px 14px", background: "rgba(13,184,126,.08)", borderTop: "1px solid rgba(255,255,255,.06)" }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 4 }}>
                {t("sidebar.clickToHide")}
              </div>
              <button className="btn btn-out btn-xs" style={{ marginTop: 4, width: "100%", justifyContent: "center", background: "transparent", borderColor: "rgba(255,255,255,.15)", color: "rgba(255,255,255,.5)" }} onClick={() => { setHiddenNav(new Set()); localStorage.removeItem("kb_hidden_nav"); }}>
                {t("sidebar.resetAll")}
              </button>
            </div>
          )}
        </nav>

        {/* ── Main ────────────────────────────────────────────── */}
        <div className="main">

          {/* Topbar */}
          <div className="topbar">
            <button className="tb-menu" onClick={() => setSidebar(true)}>
              <Menu size={18} />
            </button>
            <div className="tb-bc">{CRUMBS[page] ?? "—"}</div>
            <div className="tb-search">
              <Search size={14} color="var(--ghost)" />
              <input
                type="text"
                placeholder={SEARCH_PLACEHOLDER[page] ?? t("common.search")}
                value={globalSearch}
                onChange={e => setGlobalSearch(e.target.value)}
              />
              {globalSearch && (
                <button onClick={() => setGlobalSearch("")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)", display: "flex" }}>
                  <X size={12} />
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {/* Theme toggle */}
              <button
                className="tb-btn"
                title={`${t("acct.colorTheme")}: ${theme}`}
                onClick={() => handleThemeChange(theme === "light" ? "dark" : theme === "dark" ? "system" : "light")}
              >
                {theme === "dark" ? <Moon size={15} color="var(--ink)" /> : theme === "light" ? <Sun size={15} color="var(--ink)" /> : <Monitor size={15} color="var(--ink)" />}
              </button>
              {/* Notifications bell */}
              <div style={{ position: "relative" }} ref={notifRef}>
                <button
                  className="tb-btn"
                  onClick={() => {
                    if (!notifOpen) reloadNotifs();
                    setNotifOpen(v => !v);
                  }}
                  style={{ position: "relative" }}
                >
                  <Bell size={16} />
                  {unreadCount > 0 && (
                    <div style={{
                      position: "absolute", top: -4, right: -4,
                      minWidth: 16, height: 16, borderRadius: 8,
                      background: "var(--rose)", border: "2px solid white",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9, fontWeight: 800, color: "white", fontFamily: "var(--fm)",
                      padding: "0 3px",
                    }}>
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </div>
                  )}
                </button>
                {notifOpen && (
                  <NotifPanel
                    notifs={notifs}
                    loading={notifLoading}
                    unreadCount={unreadCount}
                    seenNotifs={seenNotifs}
                    onClose={() => setNotifOpen(false)}
                    onNavigate={navigate}
                    onRefresh={reloadNotifs}
                    onMarkOneSeen={markOneSeen}
                    onMarkAllRead={() => markAllSeen(notifs)}
                  />
                )}
              </div>

              {/* Account settings shortcut */}
              <button className="tb-btn" onClick={() => navigate("account")} title={t("topbar.accountSettings")}>
                <UserCircle size={16} />
              </button>

              {/* Broadcast shortcut */}
              <button className="btn-broadcast" onClick={() => navigate("announcements")}>
                <Megaphone size={14} /> {t("topbar.broadcast")}
              </button>
            </div>
          </div>

          {/* Page content */}
          <div className="content">
            {PAGES[page]}
          </div>

          <AppFooter onNavigate={navigate} />
        </div>
      </div>

      {/* Toast notifications */}
      <Toast toast={toast} />
    </>
  );
}
